import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Container } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { homeSliderService } from '../services/api';
import { ASSETS, getImageUrl } from '../utils/assets';

const BRAND_GREEN = '#477C2E';
const FALLBACK_SLIDE = {
  id: 'fallback',
  subtitle: 'Always donate for childrens',
  title: 'Lead a Helping Hand to Those in Need',
  button_text: 'Donate Now',
  button_link: '/campaigns',
  image_url: ASSETS.hero,
};

const AUTO_MS = 6000;

const GRADIENT_OVERLAY =
  'linear-gradient(105deg, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.22) 45%, rgba(0,0,0,0.12) 65%, rgba(0,0,0,0.28) 100%)';

function resolveNavTarget(link, navigate) {
  const raw = (link || '/campaigns').trim();
  if (!raw) {
    navigate('/campaigns');
    return;
  }
  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    window.location.href = raw;
    return;
  }
  navigate(raw.startsWith('/') ? raw : `/${raw}`);
}

function slideImageUrl(slide) {
  return getImageUrl(slide?.image_url) || ASSETS.hero || '';
}

const HomeHeroSlider = () => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    homeSliderService
      .getAll()
      .then((res) => {
        const list = res.data?.data ?? res.data ?? [];
        setSlides(Array.isArray(list) ? list : []);
      })
      .catch(() => setSlides([]))
      .finally(() => setLoading(false));
  }, []);

  const items = slides.length > 0 ? slides : [FALLBACK_SLIDE];
  const current = items[index] || items[0];

  const goTo = useCallback(
    (next) => {
      setIndex((i) => {
        const len = items.length;
        if (len <= 1) return 0;
        return ((next % len) + len) % len;
      });
    },
    [items.length]
  );

  useEffect(() => {
    if (items.length <= 1) return undefined;
    const t = setInterval(() => goTo(index + 1), AUTO_MS);
    return () => clearInterval(t);
  }, [items.length, index, goTo]);

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        maxWidth: '100vw',
        minHeight: { xs: 420, sm: 500, md: 580 },
        height: { xs: '72vh', sm: '75vh', md: 'min(78vh, 680px)' },
        maxHeight: { md: 680 },
        overflow: 'hidden',
        bgcolor: '#1a1a1a',
      }}
    >
      {/* Full-width background slides */}
      {items.map((slide, i) => (
        <Box
          key={slide.id}
          aria-hidden={i !== index}
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: slideImageUrl(slide) ? `url(${slideImageUrl(slide)})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: i === index ? 1 : 0,
            transition: 'opacity 1s ease-in-out',
            transform: i === index ? 'scale(1)' : 'scale(1.03)',
            ...(i === index && {
              animation: 'heroKenBurns 8s ease-out forwards',
            }),
            '@keyframes heroKenBurns': {
              from: { transform: 'scale(1.04)' },
              to: { transform: 'scale(1)' },
            },
          }}
        />
      ))}

      {/* Light black gradient for readable text */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: GRADIENT_OVERLAY,
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.22) 0%, transparent 50%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      />

      {/* Content on image */}
      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 2.5, sm: 3 },
        }}
      >
        <Box
          sx={{
            py: { xs: 6, md: 8 },
            maxWidth: { xs: '100%', sm: 640, md: 720 },
            animation: 'heroFadeUp 0.7s ease-out',
            '@keyframes heroFadeUp': {
              from: { opacity: 0, transform: 'translateY(16px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {current.subtitle ? (
            <Typography
              key={`sub-${current.id}-${index}`}
              sx={{
                fontFamily: '"Caveat", cursive',
                fontWeight: 700,
                fontSize: { xs: 24, sm: 28, md: 34 },
                lineHeight: 1.1,
                color: 'rgba(255,255,255,0.95)',
                mb: { xs: 1.5, md: 2 },
                textShadow: '0 2px 12px rgba(0,0,0,0.25)',
              }}
            >
              {current.subtitle}
            </Typography>
          ) : null}

          <Typography
            key={`title-${current.id}-${index}`}
            component="h1"
            sx={{
              fontFamily: '"Nunito", sans-serif',
              fontWeight: 900,
              fontSize: { xs: 30, sm: 40, md: 52, lg: 58 },
              lineHeight: { xs: 1.2, md: 1.15 },
              color: '#fff',
              mb: { xs: 2.5, md: 3.5 },
              textShadow: '0 4px 24px rgba(0,0,0,0.35)',
              letterSpacing: '-0.01em',
            }}
          >
            {current.title}
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={() => resolveNavTarget(current.button_link, navigate)}
              sx={{
                height: { xs: 48, md: 52 },
                borderRadius: '999px',
                backgroundColor: BRAND_GREEN,
                color: '#fff',
                fontFamily: '"Poppins", sans-serif',
                fontWeight: 600,
                fontSize: { xs: 13, md: 14 },
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                px: { xs: 3, md: 4 },
                boxShadow: '0 8px 28px rgba(71, 124, 46, 0.45)',
                border: '1px solid rgba(255,255,255,0.2)',
                '&:hover': {
                  backgroundColor: '#3d6a28',
                  boxShadow: '0 12px 32px rgba(71, 124, 46, 0.55)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.25s ease',
              }}
            >
              {current.button_text || 'Donate Now'}
            </Button>

            {items.length > 1 && (
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                {items.map((s, i) => (
                  <Box
                    key={s.id}
                    onClick={() => setIndex(i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setIndex(i)}
                    aria-label={`Go to slide ${i + 1}`}
                    sx={{
                      width: i === index ? 32 : 10,
                      height: 10,
                      borderRadius: 5,
                      bgcolor: i === index ? BRAND_GREEN : 'rgba(255,255,255,0.4)',
                      cursor: 'pointer',
                      transition: 'all 0.35s ease',
                      boxShadow: i === index ? '0 0 12px rgba(71,124,46,0.6)' : 'none',
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Container>

      {items.length > 1 && (
        <>
          <IconButton
            onClick={() => goTo(index - 1)}
            aria-label="Previous slide"
            sx={{
              position: 'absolute',
              left: { xs: 8, sm: 16, md: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(8px)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
            }}
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => goTo(index + 1)}
            aria-label="Next slide"
            sx={{
              position: 'absolute',
              right: { xs: 8, sm: 16, md: 24 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 3,
              width: { xs: 40, md: 48 },
              height: { xs: 40, md: 48 },
              bgcolor: 'rgba(255,255,255,0.12)',
              backdropFilter: 'blur(8px)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.25)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.22)' },
            }}
          >
            <ChevronRight />
          </IconButton>
        </>
      )}

      {!loading && slides.length === 0 && <Box sx={{ display: 'none' }} aria-hidden />}
    </Box>
  );
};

export default HomeHeroSlider;
